import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchWithAuth } from "../../utils/api";
import { swalError, swalSuccess } from "../../utils/swalAlert";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Navbar from "./Navbar";
import Table from "./Table";
import "../../App.css";

const formatNumber = (num) => {
  return new Intl.NumberFormat("en-IN").format(num);
};

const typeOptions = [
  { value: "Grader", label: "Grader", icon: "📝" },
  { value: "MCQAnswerPaperParser", label: "MCQ Parser", icon: "📋" },
  { value: "QBGenerator", label: "QB Generator", icon: "❓" },
  { value: "QBExtractor", label: "QB Extractor", icon: "🔍" },
  { value: "SolutionGenerator", label: "Solution Generator", icon: "💡" },
  { value: "ImageGenerator", label: "Image Generator", icon: "🖼️" },
  { value: "ScoreSummarizer", label: "Performance Report", icon: "📈" },
];


/**
 * CreditUsage - Reusable component for displaying credit usage data
 *
 * @param {Object} props
 * @param {'superadmin' | 'admin'} props.role - User role (determines API endpoint and UI)
 * @param {string} props.instituteId - Institute ID (required for admin role)
 * @param {'Grader' | 'QBGenerator' | 'ImageGenerator' | 'ScoreSummarizer'} props.creditType - Type of credit usage
 * @param {boolean} props.showTypeSelector - Show type selector toggle (default: true for superadmin)
 * @param {boolean} props.showInstituteName - Show institute name column (default: true for superadmin)
 * @param {Array} props.breadcrumbs - Navigation breadcrumbs
 */
export default function CreditUsage({
  role = "admin",
  instituteId = null,
  creditType = "Grader",
  showTypeSelector = null,
  showInstituteName = null,
  breadcrumbs = [],
}) {
  // Derive default values based on role
  const displayTypeSelector = showTypeSelector ?? role === "superadmin";
  const displayInstituteName = showInstituteName ?? role === "superadmin";

  const [institute, setInstitute] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [rangeOption, setRangeOption] = useState("Today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState(creditType || "Grader");

  const getLastPurchasedDate = useCallback(() => {
    if (selectedType === "Grader") {
      return institute?.combinedGradingBlockSize?.lastPurchasedDate || null;
    }

    if (selectedType === "QBGenerator") {
      return institute?.questionBlockSize?.lastPurchasedDate || null;
    }

    return null;
  }, [selectedType, institute]);

  const tableRef = useRef(null);
  const [currentTableData, setCurrentTableData] = useState([]);

  // Export States
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState(null); // 'summarized' or 'detailed'
  const [exportProgress, setExportProgress] = useState({
    step: 0,
    message: "",
  });
  const [isExportInProgress, setIsExportInProgress] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const fetchInstituteDetails = async () => {
      const serverUrl = process.env.REACT_APP_SERVER_URL;
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${instituteId}`,
          {
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) {
          throw new Error("Failed to fetch institute details");
        }
        const data = await response.json();
        setInstitute(data);
      } catch (error) {
        console.error(error.message)
      }
    };
    if (!instituteId) {
      setInstitute({});
      return;
    }
    fetchInstituteDetails();
  }, [instituteId, setInstitute]);

  const baseDateFilterOptions = [
    { value: "Today", label: "Today" },
    { value: "Last 24 Hours", label: "Last 24 Hours" },
    { value: "This Month", label: "This Month" },
    { value: "Custom", label: "Custom" },
  ];

  const dateFilterOptions = [
    ...baseDateFilterOptions.slice(0, 3),
    ...(getLastPurchasedDate()
      ? [{ value: "From Last Purchased", label: "From Last Purchased" }]
      : []),
    baseDateFilterOptions[3],
  ];

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 10);
  }, []);

  const displayDateOnly = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const getTimeRangeText = useCallback(() => {
    if (rangeOption === "Custom") {
      return `${displayDateOnly(startDate)} - ${displayDateOnly(endDate)}`;
    }
    return rangeOption;
  }, [rangeOption, startDate, endDate, displayDateOnly]);

  const setTodayRange = useCallback(() => {
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    setStartDate(userDate(startOfDay));
    setEndDate(userDate(today));
    setRangeOption("Today");
  }, [userDate]);

  const handlePresetSelect = useCallback((option) => {
    setRangeOption(option);
    const now = new Date();
    let start, end;

    switch (option) {
      case "Today":
        // From today 12:00 AM to now
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = now;
        break;
      case "Last 24 Hours":
        // Rolling 24-hour window from current time
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        end = now;
        break;
      case "This Month":
        // From 1st of current month to now
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case "From Last Purchased": {
        const lastPurchasedDate = getLastPurchasedDate();

        if (!lastPurchasedDate) {
          return;
        }

        start = new Date(lastPurchasedDate);
        end = now;
        break;
      }
      case "Custom":
      default:
        return;
    }

    const formatDate = (date) => {
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60000);
      return localDate.toISOString().slice(0, 10);
    };

    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  }, [getLastPurchasedDate]);

  const filteredData = data;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        type: selectedType,
      });

      if (role === "admin" && instituteId) {
        params.append("instituteId", instituteId);
      }

      if (startDate && endDate) {
        params.append("fromDate", startDate);
        const endDateObj = new Date(endDate);
        endDateObj.setDate(endDateObj.getDate() + 1);
        const adjustedEndDate = endDateObj.toISOString().slice(0, 10);
        params.append("toDate", adjustedEndDate);
      }

      const response = await fetchWithAuth(
        `${process.env.REACT_APP_SERVER_URL
        }/api/token-usage/get-all?${params.toString()}`,
        { credentials: "include" },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch credit usage data");
      }

      const responseData = await response.json();
      setData(responseData);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedType, role, instituteId, startDate, endDate]);

  // Initial load - set default date range
  useEffect(() => {
    setTodayRange();
  }, [setTodayRange]);

  // Fetch data when dates or selectedType changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [fetchData, startDate, endDate, selectedType]);

  const handleTableDataFiltered = (filteredTableData) => {
    if (
      JSON.stringify(filteredTableData) !== JSON.stringify(currentTableData)
    ) {
      setCurrentTableData(filteredTableData);
    }
  };

  // Calculate summary statistics for modern cards
  // SuperAdmin: Group by Institute, Admin: Group by Instructor
  const summaryStats = useMemo(() => {
    const dataToAnalyze =
      currentTableData.length > 0 ? currentTableData : filteredData;
    const statsMap = new Map();

    dataToAnalyze.forEach((item) => {
      // For admin role, group by instructor; for superadmin, group by institute
      let groupKey, groupName;
      if (role === "admin") {
        groupKey = item.instructorId || item.instructorName || "Unknown";
        groupName = item.instructorName || "Unknown Instructor";
      } else {
        groupKey = item.instituteId || item.instituteName || "Unknown";
        groupName = item.instituteName || "Unknown Institute";
      }

      if (!statsMap.has(groupKey)) {
        statsMap.set(groupKey, {
          name: groupName,
          id: groupKey,
          papersGraded: 0,
          creditsUsed: 0,
          qbGenerated: 0,
          questionsGenerated: 0,
          imagesGenerated: 0,
          performanceSummarized: 0,
          mcqParsed: 0,
          qbExtracted: 0,
          solutionsGenerated: 0,
        });
      }

      const stats = statsMap.get(groupKey);

      // Calculate credits used
      let credits = 1;
      if (item.creditUsage != null && item.creditUsage !== 0) {
        credits = item.creditUsage;
      } else if (selectedType === "QBGenerator" || selectedType === "QBExtractor" || selectedType === "SolutionGenerator") {
        credits = item.questionCount ?? 1;
      }
      stats.creditsUsed += credits;

      // Type-specific counts
      if (selectedType === "Grader") {
        stats.papersGraded += 1;
      } else if (selectedType === "MCQAnswerPaperParser") {
        stats.mcqParsed += 1;
      } else if (selectedType === "QBGenerator") {
        stats.qbGenerated += 1;
        stats.questionsGenerated += item.questionCount ?? 0;
      } else if (selectedType === "QBExtractor") {
        stats.qbExtracted += 1;
      } else if (selectedType === "SolutionGenerator") {
        stats.solutionsGenerated += 1;
      } else if (selectedType === "ImageGenerator") {
        stats.imagesGenerated += 1;
      } else if (selectedType === "ScoreSummarizer") {
        stats.performanceSummarized += 1;
      }
    });

    return Array.from(statsMap.values()).sort(
      (a, b) => b.creditsUsed - a.creditsUsed,
    );
  }, [currentTableData, filteredData, selectedType, role]);

  // Get the label for the count based on type
  const getCountLabel = useCallback(() => {
    switch (selectedType) {
      case "Grader":
        return "Papers Graded";
      case "MCQAnswerPaperParser":
        return "MCQ Papers Parsed";
      case "QBGenerator":
        return "QB Generated";
      case "QBExtractor":
        return "QB Extracted";
      case "SolutionGenerator":
        return "Solutions Generated";
      case "ImageGenerator":
        return "Images Generated";
      case "ScoreSummarizer":
        return "Summaries Generated";
      default:
        return "Items";
    }
  }, [selectedType]);

  // Get the count value based on type
  const getCountValue = useCallback(
    (instStats) => {
      switch (selectedType) {
        case "Grader":
          return instStats.papersGraded;
        case "MCQAnswerPaperParser":
          return instStats.mcqParsed;
        case "QBGenerator":
          return instStats.qbGenerated;
        case "QBExtractor":
          return instStats.qbExtracted;
        case "SolutionGenerator":
          return instStats.solutionsGenerated;
        case "ImageGenerator":
          return instStats.imagesGenerated;
        case "ScoreSummarizer":
          return instStats.performanceSummarized;
        default:
          return 0;
      }
    },
    [selectedType],
  );

  const downloadSummarizedExport = useCallback(async () => {
    try {
      if (summaryStats.length === 0) {
        swalError("No summary data available to export");
        return;
      }

      setIsExportInProgress(true);
      setExportProgress({ step: 1, message: "Initializing Excel workbook..." });
      await new Promise((r) => setTimeout(r, 200));

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Usage Summary");

      // Headers
      const headers = [
        role === "superadmin" ? "Institute Name" : "Instructor Name",
        "Type",
        getCountLabel(),
        "Credits Used",
      ];

      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F09A21" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      setExportProgress({ step: 2, message: "Processing summary data..." });
      await new Promise((r) => setTimeout(r, 200));

      // Data Rows
      summaryStats.forEach((stat) => {
        const row = worksheet.addRow([
          stat.name,
          selectedType,
          getCountValue(stat),
          stat.creditsUsed,
        ]);
        row.getCell(3).alignment = { horizontal: "center" }; // Count
        row.getCell(4).alignment = { horizontal: "center" }; // Credits
      });

      // Column widths
      worksheet.columns = [
        { width: 30 }, // Name
        { width: 15 }, // Type
        { width: 15 }, // Count
        { width: 15 }, // Credits
      ];

      setExportProgress({ step: 3, message: "Generating file..." });
      await new Promise((r) => setTimeout(r, 200));

      const timeRange = getTimeRangeText().replace(/[<>:"/\\|?*]/g, "-");
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `Credit_Usage_Summary_${selectedType}_${timeRange}_${timestamp}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);

      setExportProgress({ step: 4, message: "Export complete!" });
      await new Promise((r) => setTimeout(r, 500));
      setIsExportModalOpen(false);
      swalSuccess("Summary export downloaded successfully");
    } catch (err) {
      console.error("Summary export error:", err);
      swalError("Failed to export summary data");
    } finally {
      setIsExportInProgress(false);
    }
  }, [
    summaryStats,
    role,
    selectedType,
    getCountLabel,
    getCountValue,
    getTimeRangeText,
  ]);

  const downloadDetailedExport = useCallback(async () => {
    try {
      const dataToExport =
        currentTableData.length > 0 ? currentTableData : filteredData;

      if (!dataToExport || dataToExport.length === 0) {
        swalError("No detailed data available to export");
        return;
      }

      setIsExportInProgress(true);
      setExportProgress({ step: 1, message: "Initializing Excel workbook..." });
      await new Promise((r) => setTimeout(r, 200));

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Detailed Usage");

      const uniqueInstitutes = new Set(
        dataToExport.map((item) => item.instituteName),
      );
      const isInstituteFiltered =
        uniqueInstitutes.size === 1 && dataToExport.length > 0;

      // Define columns based on existing CSV logic but for Excel
      const baseHeaders = {
        Grader: [
          "Level",
          "Test Type",
          "Class",
          "Instructor",
          "Test",
          "Student Name",
          "Graded At",
          "Page Count",
          "Credits Used",
        ],
        MCQAnswerPaperParser: [
          "Level",
          "Test Type",
          "Class",
          "Instructor",
          "Test",
          "Student Name",
          "Parsed At",
          "Page Count",
          "Credits Used",
        ],
        QBGenerator: [
          "Level",
          "Class",
          "Instructor",
          "QB Name",
          "Question Count",
          "Generated At",
          "Credits Used",
        ],
        QBExtractor: [
          "Level",
          "Class",
          "Instructor",
          "QB Name",
          "Question Count",
          "Extracted At",
          "Credits Used",
        ],
        SolutionGenerator: [
          "Level",
          "Class",
          "Instructor",
          "QB Name",
          "Question Count",
          "Generated At",
          "Credits Used",
        ],
        ImageGenerator: [
          "Level",
          "Class",
          "Instructor",
          "QB ID",
          "QB Name",
          "Question ID",
          "Generated At",
          "Credits Used",
        ],
        ScoreSummarizer: [
          "Level",
          "Test Type",
          "Class",
          "Instructor",
          "Test",
          "Student Name",
          "Summarized At",
          "Credits Used",
        ],
      };

      const columns =
        displayInstituteName && !isInstituteFiltered
          ? ["Institute Name", ...(baseHeaders[selectedType] || baseHeaders.Grader)]
          : (baseHeaders[selectedType] || baseHeaders.Grader);

      const headerRow = worksheet.addRow(columns);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F09A21" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      setExportProgress({
        step: 2,
        message: `Processing ${dataToExport.length} records...`,
      });
      await new Promise((r) => setTimeout(r, 200));

      const rows = dataToExport.map((item) => {
        const baseRowData = {
          Grader: [
            item.level || "N/A",
            item.testType || "N/A",
            item.className || "N/A",
            item.instructorName || "[DELETED]",
            item.testName || "[DELETED]",
            item.studentName || "[DELETED]",
            new Date(item.createdAt).toLocaleString(),
            item.filePageCount || 0,
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
          ],
          MCQAnswerPaperParser: [
            item.level || "N/A",
            item.testType || "N/A",
            item.className || "N/A",
            item.instructorName || "[DELETED]",
            item.testName || "[DELETED]",
            item.studentName || "[DELETED]",
            new Date(item.createdAt).toLocaleString(),
            item.filePageCount || 0,
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
          ],
          QBGenerator: [
            item.level || "N/A",
            item.className || "N/A",
            item.instructorName || "[DELETED]",
            item.questionBankName || "N/A",
            item.questionCount || "N/A",
            new Date(item.createdAt).toLocaleString(),
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : (item.questionCount ?? 1),
          ],
          QBExtractor: [
            item.level || "N/A",
            item.className || "N/A",
            item.instructorName || "[DELETED]",
            item.questionBankName || "N/A",
            item.questionCount || "N/A",
            new Date(item.createdAt).toLocaleString(),
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : (item.questionCount ?? 1),
          ],
          SolutionGenerator: [
            item.level || "N/A",
            item.className || "N/A",
            item.instructorName || "[DELETED]",
            item.questionBankName || "N/A",
            item.questionCount || "N/A",
            new Date(item.createdAt).toLocaleString(),
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : (item.questionCount ?? 1),
          ],
          ImageGenerator: [
            item.level || "N/A",
            item.className || "N/A",
            item.instructorName || "[DELETED]",
            item.questionBankName || "N/A",
            item.questionBankId || "N/A",
            item.questionId || "N/A",
            new Date(item.createdAt).toLocaleString(),
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
          ],
          ScoreSummarizer: [
            item.level || "N/A",
            item.testType || "N/A",
            item.className || "N/A",
            item.instructorName || "[DELETED]",
            item.testName || "[DELETED]",
            item.studentName || "[DELETED]",
            new Date(item.createdAt).toLocaleString(),
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
          ],
        };

        return displayInstituteName && !isInstituteFiltered
          ? [item.instituteName || "N/A", ...(baseRowData[selectedType] || baseRowData.Grader)]
          : (baseRowData[selectedType] || baseRowData.Grader);
      });

      worksheet.addRows(rows);

      // Auto-width columns (simple estimation)
      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      setExportProgress({ step: 3, message: "Generating file..." });
      await new Promise((r) => setTimeout(r, 200));

      const timeRange = getTimeRangeText().replace(/[<>:"/\\|?*]/g, "-");
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `Credit_Usage_Detailed_${selectedType}_${timeRange}_${timestamp}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);

      setExportProgress({ step: 4, message: "Export complete!" });
      await new Promise((r) => setTimeout(r, 500));
      setIsExportModalOpen(false);
      swalSuccess("Detailed export downloaded successfully");
    } catch (err) {
      console.error("Detailed export error:", err);
      swalError("Failed to export detailed data");
    } finally {
      setIsExportInProgress(false);
    }
  }, [
    currentTableData,
    filteredData,
    selectedType,
    displayInstituteName,
    getTimeRangeText,
  ]);

  // Get columns based on type
  const getColumns = useCallback(() => {
    const baseColumns = {
      Grader: [
        { key: "level", label: "Level", filter: true },
        { key: "testType", label: "Test Type", filter: true },
        { key: "class", label: "Class", filter: true },
        { key: "instructor", label: "Instructor", filter: true },
        { key: "test", label: "Test", filter: true },
        { key: "studentName", label: "Student Name", filter: true },
        {
          key: "gradedAt",
          label: "Graded At",
          render: (item) => {
            const date = new Date(item.gradedAt);
            return (
              <span>
                {date.toLocaleDateString(navigator.language, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}{" "}
                at{" "}
                {date.toLocaleTimeString(navigator.language, {
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </span>
            );
          },
        },
        {
          key: "filePageCount",
          label: "Page Count",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.filePageCount)}
            </span>
          ),
        },
        {
          key: "creditsUsed",
          label: "Credits Used",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.creditsUsed)}
            </span>
          ),
        },
      ],
      MCQAnswerPaperParser: [
        { key: "level", label: "Level", filter: true },
        { key: "testType", label: "Test Type", filter: true },
        { key: "class", label: "Class", filter: true },
        { key: "instructor", label: "Instructor", filter: true },
        { key: "test", label: "Test", filter: true },
        { key: "studentName", label: "Student Name", filter: true },
        {
          key: "parsedAt",
          label: "Parsed At",
          render: (item) => {
            const date = new Date(item.parsedAt);
            return (
              <span>
                {date.toLocaleDateString(navigator.language, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}{" "}
                at{" "}
                {date.toLocaleTimeString(navigator.language, {
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </span>
            );
          },
        },
        {
          key: "filePageCount",
          label: "Page Count",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.filePageCount)}
            </span>
          ),
        },
        {
          key: "creditsUsed",
          label: "Credits Used",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.creditsUsed)}
            </span>
          ),
        },
      ],
      QBGenerator: [
        { key: "level", label: "Level", filter: true },
        { key: "class", label: "Class", filter: true },
        { key: "instructor", label: "Instructor", filter: true },
        { key: "qbName", label: "QB Name", filter: true },
        { key: "qCount", label: "Question Count", filter: true },
        {
          key: "genAt",
          label: "Generated At",
          render: (item) => (
            <span>{new Date(item.genAt).toLocaleString()}</span>
          ),
        },
        {
          key: "creditsUsed",
          label: "Credits Used",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.creditsUsed)}
            </span>
          ),
        },
      ],
      QBExtractor: [
        { key: "level", label: "Level", filter: true },
        { key: "class", label: "Class", filter: true },
        { key: "instructor", label: "Instructor", filter: true },
        { key: "qbName", label: "QB Name", filter: true },
        { key: "qCount", label: "Question Count", filter: true },
        {
          key: "genAt",
          label: "Extracted At",
          render: (item) => (
            <span>{new Date(item.genAt).toLocaleString()}</span>
          ),
        },
        {
          key: "creditsUsed",
          label: "Credits Used",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.creditsUsed)}
            </span>
          ),
        },
      ],
      SolutionGenerator: [
        { key: "level", label: "Level", filter: true },
        { key: "class", label: "Class", filter: true },
        { key: "instructor", label: "Instructor", filter: true },
        { key: "qbName", label: "QB Name", filter: true },
        { key: "qCount", label: "Question Count", filter: true },
        {
          key: "genAt",
          label: "Generated At",
          render: (item) => (
            <span>{new Date(item.genAt).toLocaleString()}</span>
          ),
        },
        {
          key: "creditsUsed",
          label: "Credits Used",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.creditsUsed)}
            </span>
          ),
        },
      ],
      ImageGenerator: [
        { key: "level", label: "Level", filter: true },
        { key: "class", label: "Class", filter: true },
        { key: "instructor", label: "Instructor", filter: true },
        { key: "qbId", label: "QB ID", filter: true },
        { key: "qbName", label: "QB Name", filter: true },
        { key: "qID", label: "Question ID", filter: true },
        {
          key: "genAt",
          label: "Generated At",
          render: (item) => (
            <span>{new Date(item.genAt).toLocaleString()}</span>
          ),
        },
        {
          key: "creditsUsed",
          label: "Credits Used",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.creditsUsed)}
            </span>
          ),
        },
      ],
      ScoreSummarizer: [
        { key: "level", label: "Level", filter: true },
        { key: "testType", label: "Test Type", filter: true },
        { key: "class", label: "Class", filter: true },
        { key: "instructor", label: "Instructor", filter: true },
        { key: "test", label: "Test", filter: true },
        { key: "studentName", label: "Student Name", filter: true },
        {
          key: "summarizedAt",
          label: "Summarized At",
          render: (item) => {
            const date = new Date(item.summarizedAt);
            return (
              <span>
                {date.toLocaleDateString(navigator.language, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}{" "}
                at{" "}
                {date.toLocaleTimeString(navigator.language, {
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </span>
            );
          },
        },
        {
          key: "creditsUsed",
          label: "Credits Used",
          render: (item) => (
            <span className="token-count">
              {formatNumber(item.creditsUsed)}
            </span>
          ),
        },
      ],
    };

    const columns = baseColumns[selectedType] || baseColumns.Grader;

    if (displayInstituteName) {
      return [
        { key: "instituteName", label: "Institute Name", filter: true },
        ...columns,
      ];
    }

    return columns;
  }, [selectedType, displayInstituteName]);

  // Transform data for table
  const getTableData = useCallback(() => {
    return filteredData.map((item) => {
      const baseData = {
        ...item,
        level: item.level || "N/A",
        class: item.className || "N/A",
        instructor: item.instructorName || "[DELETED]",
      };

      if (selectedType === "Grader") {
        return {
          ...baseData,
          testType: item.testType || "N/A",
          test: item.testName || "[DELETED]",
          studentName: item.studentName || "[DELETED]",
          gradedAt: item.createdAt,
          filePageCount: item.filePageCount || 0,
          creditsUsed:
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
        };
      } else if (selectedType === "MCQAnswerPaperParser") {
        return {
          ...baseData,
          testType: item.testType || "N/A",
          test: item.testName || "[DELETED]",
          studentName: item.studentName || "[DELETED]",
          parsedAt: item.createdAt,
          filePageCount: item.filePageCount || 0,
          creditsUsed:
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
        };
      } else if (selectedType === "QBGenerator") {
        return {
          ...baseData,
          qbName: item.questionBankName,
          qCount: item.questionCount,
          genAt: item.createdAt,
          creditsUsed:
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : (item.questionCount ?? 1),
        };
      } else if (selectedType === "QBExtractor") {
        return {
          ...baseData,
          qbName: item.questionBankName,
          qCount: item.questionCount,
          genAt: item.createdAt,
          creditsUsed:
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : (item.questionCount ?? 1),
        };
      } else if (selectedType === "SolutionGenerator") {
        return {
          ...baseData,
          qbName: item.questionBankName,
          qCount: item.questionCount,
          genAt: item.createdAt,
          creditsUsed:
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : (item.questionCount ?? 1),
        };
      } else if (selectedType === "ImageGenerator") {
        return {
          ...baseData,
          qbId: item.questionBankId,
          qbName: item.questionBankName,
          qID: item.questionId,
          genAt: item.createdAt,
          creditsUsed:
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
        };
      } else if (selectedType === "ScoreSummarizer") {
        return {
          ...baseData,
          testType: item.testType || "N/A",
          test: item.testName || "[DELETED]",
          studentName: item.studentName || "[DELETED]",
          summarizedAt: item.createdAt,
          creditsUsed:
            item.creditUsage != null && item.creditUsage !== 0
              ? item.creditUsage
              : 1,
        };
      }

      return baseData;
    });
  }, [filteredData, selectedType]);

  const getTableCaption = () => {
    const prefix = role === "superadmin" ? "SuperAdmin " : "";
    let typeLabel = "Credit Usage";
    if (selectedType === "Grader") {
      typeLabel = "Grading";
    } else if (selectedType === "MCQAnswerPaperParser") {
      typeLabel = "MCQ Parser";
    } else if (selectedType === "QBGenerator") {
      typeLabel = "Question Bank";
    } else if (selectedType === "QBExtractor") {
      typeLabel = "QB Extractor";
    } else if (selectedType === "SolutionGenerator") {
      typeLabel = "Solution Generator";
    } else if (selectedType === "ScoreSummarizer") {
      typeLabel = "Performance Report";
    } else if (selectedType === "ImageGenerator") {
      typeLabel = "Image Generator";
    }
    return `${prefix}${typeLabel} Credit Usage`;
  };

  return (
    <>
      <Navbar breadcrumbs={breadcrumbs} />
      <div className="manage-token">
        {/* Type Selector - Only for SuperAdmin */}
        {displayTypeSelector && (
          <motion.div
            className="controls-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="type-selector">
              <label>Usage Type:</label>
              <div className="type-buttons">
                {typeOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`type-btn ${
                      selectedType === option.value ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedType(option.value);
                      handlePresetSelect("Today");
                    }}
                  >
                    <span className="type-icon">{option.icon}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Date Range Selector */}
        <div className="time-range-wrapper">
          <button
            onClick={() => setShowDatePopup((prev) => !prev)}
            className="time-range-btn"
            aria-expanded={showDatePopup}
            aria-haspopup="true"
          >
            <span className="btn-icon">🕒</span>
            <span className="time-range-text">{getTimeRangeText()}</span>
            <span className={`chevron ${showDatePopup ? "open" : ""}`}>▼</span>
          </button>

          {showDatePopup && (
            <>
              <div
                className="popup-overlay"
                onClick={() => setShowDatePopup(false)}
              />
              <div className="time-range-popup">
                <div className="popup-header">
                  <h3>Select Time Range</h3>
                  <button
                    className="close-btn"
                    onClick={() => setShowDatePopup(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="preset-section">
                  <div className="preset-grid">
                    {dateFilterOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`preset-option ${
                          rangeOption === option.value ? "selected" : ""
                        }`}
                        onClick={() => handlePresetSelect(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="custom-range-section">
                  <div className="date-inputs">
                    <div className="input-group">
                      <label htmlFor="start-date">Start Date</label>
                      <input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setRangeOption("Custom");
                        }}
                        className="date-input"
                      />
                    </div>
                    <div className="input-group">
                      <label htmlFor="end-date">End Date</label>
                      <input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setRangeOption("Custom");
                        }}
                        className="date-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading credit usage data...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Error Loading Data</h2>
            <p>{error}</p>
            <button onClick={fetchData} className="retry-btn">
              <span>🔄</span> Retry
            </button>
          </div>
        )}

        {/* Main Content - Only show when not loading and no error */}
        {!loading && !error && (
          <>
            {/* Summary Cards - Institute (SuperAdmin) or Instructor (Admin) */}
            <motion.div
              className="institute-stats-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="institute-stats-scroll">
                {summaryStats.length > 0 ? (
                  summaryStats.map((statItem, index) => (
                    <motion.div
                      key={statItem.id}
                      className="institute-stat-card"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <div className="institute-card-header">
                        <span className="institute-icon">
                          {role === "admin" ? "👤" : "🏫"}
                        </span>
                        <h3 className="institute-name">{statItem.name}</h3>
                      </div>
                      <div className="institute-card-stats">
                        <div className="stat-item">
                          <span className="stat-label">{getCountLabel()}</span>
                          <span className="stat-value">
                            {formatNumber(getCountValue(statItem))}
                          </span>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                          <span className="stat-label">Credits Used</span>
                          <span className="stat-value credits">
                            {formatNumber(statItem.creditsUsed)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="no-data-card">
                    <span className="no-data-icon">📊</span>
                    <p>No data available for the selected dates</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Data Table */}
            <motion.div
              className="table-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="table-header">
                <button
                  className="export-csv-btn"
                  onClick={() => setIsExportModalOpen(true)}
                  style={{ whiteSpace: "nowrap" }}
                >
                  <span className="export-icon">📊</span>
                  <span className="export-text">Export Usage</span>
                </button>
              </div>
              <Table
                caption={getTableCaption()}
                ref={tableRef}
                data={getTableData()}
                columns={getColumns()}
                onDataFiltered={handleTableDataFiltered}
              />
            </motion.div>
          </>
        )}

        {/* Export Selection Modal */}
        <AnimatePresence>
          {isExportModalOpen && (
            <div
              className="modal-overlay"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
              onClick={(e) => {
                if (!isExportInProgress && e.target === e.currentTarget) {
                  setIsExportModalOpen(false);
                }
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="export-modal-content"
                style={{
                  backgroundColor: "white",
                  borderRadius: "16px",
                  padding: "2rem",
                  maxWidth: "450px",
                  width: "90%",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  position: "relative",
                  maxHeight: "90vh",
                  overflowY: "auto",
                }}
              >
                {/* Close X button */}
                {!isExportInProgress && (
                  <button
                    onClick={() => setIsExportModalOpen(false)}
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      background: "none",
                      border: "none",
                      fontSize: "1.5rem",
                      color: "#9ca3af",
                      cursor: "pointer",
                      padding: "0.25rem",
                      lineHeight: 1,
                      borderRadius: "4px",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#374151";
                      e.currentTarget.style.backgroundColor = "#f3f4f6";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#9ca3af";
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    ✕
                  </button>
                )}
                {!isExportInProgress ? (
                  <>
                    <h2
                      style={{
                        margin: "0 0 0.5rem 0",
                        fontSize: "1.5rem",
                        color: "#1a1a1a",
                      }}
                    >
                      Export Usage
                    </h2>
                    <p
                      style={{
                        color: "#666",
                        marginBottom: "1.5rem",
                        fontSize: "0.95rem",
                      }}
                    >
                      Choose the type of export you need:
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                      }}
                    >
                      <button
                        onClick={() => {
                          setExportType("summarized");
                          downloadSummarizedExport();
                        }}
                        style={{
                          padding: "1rem 1.5rem",
                          borderRadius: "12px",
                          border: "2px solid #e5e7eb",
                          backgroundColor: "#f9fafb",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#f09a21";
                          e.currentTarget.style.backgroundColor = "#eff6ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "1rem",
                            color: "#1a1a1a",
                            marginBottom: "0.25rem",
                          }}
                        >
                          📋 Usage Summary
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "#666" }}>
                          Excel with aggregated usage counts by{" "}
                          {role === "superadmin" ? "Institute" : "Instructor"}.
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setExportType("detailed");
                          downloadDetailedExport();
                        }}
                        style={{
                          padding: "1rem 1.5rem",
                          borderRadius: "12px",
                          border: "2px solid #e5e7eb",
                          backgroundColor: "#f9fafb",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#10b981";
                          e.currentTarget.style.backgroundColor = "#ecfdf5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "1rem",
                            color: "#1a1a1a",
                            marginBottom: "0.25rem",
                          }}
                        >
                          📊 Detailed Usage
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "#666" }}>
                          Complete Excel sheet with individual transaction logs.
                        </div>
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "1rem 0" }}>
                    <div style={{ marginBottom: "1.5rem" }}>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          repeat: Infinity,
                          duration: 1,
                          ease: "linear",
                        }}
                        style={{
                          width: "48px",
                          height: "48px",
                          border: "4px solid #e5e7eb",
                          borderTopColor:
                            exportType === "summarized" ? "#f09a21" : "#10b981",
                          borderRadius: "50%",
                          margin: "0 auto",
                        }}
                      />
                    </div>
                    <h3
                      style={{
                        margin: "0 0 0.5rem 0",
                        fontSize: "1.25rem",
                        color: "#1a1a1a",
                      }}
                    >
                      {exportType === "summarized"
                        ? "Preparing Summary"
                        : "Preparing Detailed Export"}
                    </h3>
                    <motion.p
                      key={exportProgress.message}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        color: "#666",
                        marginBottom: "1.5rem",
                        fontSize: "0.95rem",
                        minHeight: "1.5rem",
                      }}
                    >
                      {exportProgress.message}
                    </motion.p>
                    <div
                      style={{
                        width: "100%",
                        height: "6px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "3px",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: "0%" }}
                        animate={{
                          width: `${(exportProgress.step / 4) * 100}%`,
                        }}
                        transition={{ duration: 0.3 }}
                        style={{
                          height: "100%",
                          backgroundColor:
                            exportType === "summarized" ? "#f09a21" : "#10b981",
                          borderRadius: "3px",
                        }}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
